import React, { useContext, useRef, useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import "./Result.scss";
import { ReactComponent as ArrowHigh } from "../../assets/media/arrow-high.svg";
import { ReactComponent as ArrowLow } from "../../assets/media/arrow-low.svg";
import { I18nContext } from "../../context";
import {
  replacePlaceholders,
  highlightNumbers,
  useCountNumber,
} from "../../lib/helpers";
import Graph from "../Graph/Graph";
import Tooltip from "../Tooltip/Tooltip";
import Next from "../Next/Next";

/**
 * Render rank
 * @param {Object} props
 * @param {Object} props.result API response
 * @returns {React.ReactElement}
 */
function ResultRanking({ result }) {
  const { locale } = useContext(I18nContext);
  const [ref, inView] = useInView({
    threshold: 1,
  });

  const counter = useCountNumber(result.richestPerson, { isEnabled: inView });
  const text = replacePlaceholders(locale.rankingLabel, {
    rank: highlightNumbers(counter, ""),
  });

  return (
    <div className="ResultRanking" ref={ref}>
      <div className="ResultRanking__inner">
        <p
          className="ResultRanking__text"
          dangerouslySetInnerHTML={{ __html: text }}
        ></p>
      </div>
    </div>
  );
}

/**
 * Render income group
 * @param {Object} props
 * @param {Object} props.region Selected region
 * @param {Object} props.result API response
 * @returns {React.ReactElement}
 */
function ResultGroup({ region, result, income }) {
  const { locale } = useContext(I18nContext);

  const [isLoaded, setIsLoaded] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView) {
      setIsLoaded(true);
    }
  }, [inView]);

  useEffect(() => {
    setIsLoaded(false);
  }, [income.incomeSalary]);

  const counter = useCountNumber(result.incomeGroupPercentage, {
    interval: 50,
    duration: 500,
    isEnabled: inView,
  });
  const title = replacePlaceholders(locale.incomeGroupTitle, {
    group: result.incomeGroup,
  });
  const text = replacePlaceholders(
    region.Code !== "CH"
      ? locale.incomeGroupSubtitleRegion
      : locale.incomeGroupSubtitle,
    {
      percentage: counter,
      region: region.Name,
    }
  );

  return (
    <div
      className={`ResultGroup${result.isTop ? " ResultGroup--top" : ""} ${
        isLoaded ? `ResultAverage--loaded` : ""
      }`}
      ref={ref}
    >
      <div className="ResultGroup__inner">
        <p className="ResultGroup__title">{title}</p>
        <p className="ResultGroup__text">{text}</p>
      </div>
    </div>
  );
}

/**
 * Render percentage with lower income
 * @param {Object} props
 * @param {Object} props.region Selected region
 * @param {Object} props.result API response
 * @returns {React.ReactElement}
 */
function ResultPercentage({ region, result }) {
  const { locale } = useContext(I18nContext);
  const [ref, inView] = useInView({
    threshold: 0.5,
  });

  const counter = useCountNumber(result.moreIncomeThan, {
    interval: 50,
    duration: 500,
    isEnabled: inView,
  });
  const title = result.incomeTitle
    ? highlightNumbers(result.incomeTitle)
    : null;
  const text = result.incomeText
    ? highlightNumbers(result.incomeText)
    : replacePlaceholders(
        region.Code !== "CH"
          ? locale.incomePercentageTitleRegion
          : locale.incomePercentageTitle,
        {
          percentage: counter,
          region: region.Name,
        }
      );

  return (
    <div
      className={`ResultPercentage${
        result.isTop ? " ResultPercentage--top" : ""
      }`}
      ref={ref}
    >
      <div className="ResultPercentage__inner">
        {result.moreIncomeThan ? (
          <Graph className="ResultPercentage__graph" value={counter} />
        ) : null}
        {title ? (
          <p
            className="ResultPercentage__title"
            dangerouslySetInnerHTML={{ __html: title }}
          ></p>
        ) : null}
        <p
          className={`ResultPercentage__text ${
            title ? `ResultPercentage__text--small` : ""
          }`}
          dangerouslySetInnerHTML={{ __html: text }}
        ></p>
      </div>
    </div>
  );
}

/**
 * Render comparison
 * @param {Object} props
 * @param {Object} props.region Selected region
 * @param {Object} props.income Data entered
 * @returns {React.ReactElement}
 */
function ResultComparison({ income, region }) {
  const { locale } = useContext(I18nContext);
  // const salary = useIncomeSalary(income);

  const title = replacePlaceholders(
    region.Code !== "CH"
      ? locale.incomeComparisonTitleRegion
      : locale.incomeComparisonTitle,
    {
      income: highlightNumbers(`${locale.currency} ${income.incomeSalary}`),
      region: region.Name,
    }
  );

  return (
    <div className="ResultComparison">
      <div className="ResultComparison__inner">
        <p
          className="ResultComparison__title"
          dangerouslySetInnerHTML={{ __html: title }}
        ></p>
      </div>
    </div>
  );
}

/**
 * Render average
 * @param {Object} props
 * @param {Object} props.result API response
 * @param {Object} props.income Data entered
 * @returns {React.ReactElement}
 */
function ResultAverage({ result, income }) {
  const { locale } = useContext(I18nContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView) {
      setIsLoaded(true);
    }
  }, [inView]);

  useEffect(() => {
    setIsLoaded(false);
  }, [income.incomeSalary]);

  const ratio =
    income.incomeSalary && result.meanIncome
      ? (income.incomeSalary - result.meanIncome) / result.meanIncome
      : 0;
  const number = highlightNumbers(`${locale.currency} ${result.meanIncome}`);

  return (
    <div
      className={`ResultAverage ${
        ratio < 0
          ? `ResultAverage--inverted`
          : ratio > 1
          ? `ResultAverage--switched`
          : ""
      } ${isLoaded ? `ResultAverage--loaded` : ""}`}
      ref={ref}
    >
      <div
        className="ResultAverage__difference"
        style={{
          flex: Math.abs(ratio),
        }}
      ></div>
      <div className="ResultAverage__reference">
        <div className="ResultAverage__content">
          <p className="ResultAverage__title">
            <span
              className="ResultAverage__number"
              dangerouslySetInnerHTML={{ __html: number }}
            />

            {locale.incomeAverageSubtitle}
            {locale.incomeAverageHelp ? (
              <Tooltip text={locale.incomeAverageHelp} />
            ) : null}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Render distribution with upper 1% and lower 10%
 * @param {Object} props
 * @param {Object} props.region Selected region
 * @param {Object} props.result API response
 * @returns {React.ReactElement}
 */
function ResultDistribution({ region, result }) {
  const { locale } = useContext(I18nContext);
  const [refUpper, inViewUpper] = useInView({
    threshold: 0.5,
  });
  const [refLower, inViewLower] = useInView({
    threshold: 0.5,
  });

  const counterUpper = useCountNumber(result.upperIncome, {
    isEnabled: inViewUpper,
  });
  const counterLower = useCountNumber(result.lowerIncome, {
    isEnabled: inViewLower,
  });

  function wrapSuffix(text) {
    const wrapped = text.replace(
      /<\/span>(.+)/,
      '</span><span class="ResultDistribution__suffix">$1</span>'
    );

    return wrapped;
  }

  const upperTitle = wrapSuffix(
    replacePlaceholders(
      region.Code !== "CH"
        ? locale.incomeDistributionUpperTitleRegion
        : locale.incomeDistributionUpperTitle,
      {
        income: highlightNumbers(`${locale.currency} ${counterUpper}`),
        region: region.Name,
      }
    )
  );
  const lowerTitle = wrapSuffix(
    replacePlaceholders(locale.incomeDistributionLowerTitle, {
      income: highlightNumbers(`${locale.currency} ${counterLower}`),
      region: region.Name,
    })
  );

  return (
    <div className="ResultDistribution">
      <div
        className="ResultDistribution__item ResultDistribution__item--upper"
        ref={refUpper}
      >
        <div className="ResultDistribution__inner">
          <div className="ResultDistribution__content">
            <ArrowHigh className="ResultDistribution__icon" />
            <p
              className="ResultDistribution__title"
              dangerouslySetInnerHTML={{ __html: upperTitle }}
            ></p>
          </div>
        </div>
      </div>
      <div
        className="ResultDistribution__item ResultDistribution__item--lower"
        ref={refLower}
      >
        <div className="ResultDistribution__inner">
          <div className="ResultDistribution__content">
            <ArrowLow className="ResultDistribution__icon" />
            <p
              className="ResultDistribution__title"
              dangerouslySetInnerHTML={{ __html: lowerTitle }}
            ></p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Render result area
 * @param {Object} props
 * @param {Object} props.income Data entered
 * @param {Object} props.region Selected region
 * @param {Object} props.result API response
 * @param {Function} props.showNextSection Callback
 * @returns {React.ReactElement}
 */
function Result(props) {
  const percentageSection = useRef(null);
  const comparisonSection = useRef(null);
  const distributionSection = useRef(null);

  const scrollTo = (section) => {
    if (section.current) {
      section.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="Result">
      <div className="Result__section">
        {props.result.isTop ? null : <ResultRanking {...props} />}
        <ResultGroup {...props} />
        <Next onClick={() => scrollTo(percentageSection)} />
      </div>
      <div className="Result__section" ref={percentageSection}>
        <ResultPercentage {...props} />
        <Next onClick={() => scrollTo(comparisonSection)} variant="primary" />
      </div>
      <div className="Result__section" ref={comparisonSection}>
        <ResultComparison {...props} />
        <ResultAverage {...props} />
        <Next onClick={() => scrollTo(distributionSection)} />
      </div>
      <div className="Result__section" ref={distributionSection}>
        <ResultDistribution {...props} />
        <Next onClick={props.showNextSection} variant="primary" />
      </div>
    </div>
  );
}

export default Result;

export {
  ResultRanking,
  ResultGroup,
  ResultPercentage,
  ResultComparison,
  ResultAverage,
  ResultDistribution,
};
